import React, { FC, useState, useCallback } from 'react';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import {
  Box,
  Alert,
  Grid,
  makeStyles,
  Chip,
  Theme,
  Typography,
  CardHeading,
  CardGeneric,
  LabelValue,
  List,
  ListItem,
  useTheme,
  useMediaQuery,
} from '@dxb-web-ui-framework/design-system';
import { isDefined, isEmpty } from '@dxb-web-ui-framework/common/utils';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { transactionService, favouriteTransfersService } from '../../../../../../channels/secure-web/services';
import { useWizardStepData } from '../../../../../content-composer/src/contexts/ClientSideWizard/hooks';
import { ErrorContent } from '../../../../../content-composer/src/components/Partials/ErrorContent';
import { useCorporateCustomerId, useSelectedCustomerId } from '../../../../../userProfile/src/contexts/UserInfo/hooks';
import { useLanguage } from '../../../../../internationalization/src/contexts/Internationalization/hooks';
import { getRoutePath } from '../../../../../content-composer/src/components/Routes/routePaths';
import { ContentWrapper } from '../../../../../content-composer/src/components/Generic/ContentWrapper';
import { SkeletonTypography } from '../../../../../content-composer/src/components/Generic/Skeleton';
import { ConsolidatedTransactionDetails } from '../../../../../transaction/src/domain/Transaction/entities/ConsolidatedTransaction';
import { GenericDrawer } from '../../../../../content-composer/src/components/Generic/GenericDrawer';
import { ResidentBankLogo } from '../../../../../transaction/src/components/TransactionDetails/ResidentBankLogo';
import { TransactionOrderStatusChip } from '../../../../../transaction/src/components';
import { OrderStatus } from '../../../../../transaction/src/domain/Transaction/entities/OrderStatus';
import { ExchangeCalculateResponseBody } from '../../../domain/Transfer/entities/ExchangeCalculateResponseDTO';
import { NewFavouriteAppTransactionBody } from '../../../domain/FavouriteTransfer/entities/NewFavouriteAppTransactionResponseDTO';
import { exchangeAmountPredicator, useTransactionCharges } from '../../../../../transaction/src/utils';
import { TransactionDocument } from '../../../../../transaction/src/domain/Transaction/entities/TransactionDocuments';
import { TransferServiceImpl } from '../../../domain/Transfer/usecases';
import { MakeTransferResponseBody } from '../../../domain/Transfer/entities/MakeTransferResponseDTO';
import { useFundsTransferUILogicDisplayer } from '../useFundsTransferUILogicDisplayer';
import { ExchangeRatesInfo } from '../ExchangeRatesInfo';
import { useFavouriteTransfersRefetch } from '../../../contexts/FavouriteTransfers/hooks';
import { useDownloadTransactionDetails } from '../../../../../content-composer/src/hooks/useDownloadTransactionDetails';
import { OrderChargesResponseBody } from '../../../domain/Transfer/entities/OrderChargesResponseDTO';
import { TransactionDetailsDrawerFileDownload } from '../../../../../transaction/src/components/TransactionDetails/TransactionDetailsDrawer';
import { DesktopContent, MobileContent, RequestDealerRateSuccessInfo, SaveTemplateDialog } from './';

const useStyles = makeStyles<Theme>(({ palette, spacing, breakpoints }) => ({
  container: ({ isMobile }: { isMobile?: boolean }) => ({
    flex: 1,
    width: '100%',
    paddingLeft: spacing(isMobile ? 0 : 15),
    paddingRight: spacing(isMobile ? 0 : 15),
  }),
  subContainer: {
    flexFlow: 'row',
    [breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  subItemContainer: {
    flexFlow: 'column',
    display: 'flex',
  },
  list: {
    marginTop: spacing(2),
    [breakpoints.down('sm')]: {
      marginTop: spacing(0),
    },
  },
  retry: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  listItem: {
    alignItems: 'flex-start',
    display: 'inline-block',
    wordBreak: 'break-all',
  },
  oneColumn: {
    columnCount: 1,
  },
  twoColumns: {
    columnCount: 2,
  },
  defaultBackgroundColor: {
    backgroundColor: palette.background.default,
  },
  beneficiaryBankIcon: {
    marginRight: spacing(1),
  },
  leftContainer: {
    backgroundColor: palette.background.default,
    paddingBottom: spacing(15),
    [breakpoints.down('sm')]: {
      paddingBottom: spacing(8),
    },
  },
  fromToAccountContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    overflowWrap: 'break-word',
    [breakpoints.down('sm')]: {
      justifyContent: 'flex-start',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  fullHeight: ({ isMobile }: { isMobile?: boolean }) => ({
    height: isMobile ? 'auto' : '100%',
  }),
  paperClasses: {
    display: 'flex',
    paddingBottom: spacing(2),
    margin: 'auto',
    height: '100%',
    width: '100%',
  },
}));

const TransferComplete: FC<{ service: TransferServiceImpl }> = ({ service }) => {
  const [favoureTransferPostData, setFavouriteTransferPostData] = useState<null | { name: string; isShared: boolean }>(
    null,
  );
  const [timeStamp] = useState(new Date().toISOString());
  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const [isTemplateOpen, setIsTemplateOpen] = useState<boolean>(false);
  const [shouldSaveTransferAsFavourite, setShouldSaveTransferAsFavorite] = useState<boolean>(false);
  const [showTemplateSaveBtn, setShowTemplateSaveBtn] = useState<boolean>(true);

  // hooks
  const transferConfirmData = useWizardStepData('transferConfirm') as { values: GenericObject };
  const companyId = useCorporateCustomerId();
  const theme = useTheme();
  const isBelowSpecificBreakpoint = useMediaQuery(theme.breakpoints.down(1127));
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const classes = useStyles({ isMobile });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const customerId = useSelectedCustomerId();
  const language = useLanguage();
  // Favourite transfers hook
  const favouriteTransferRefetch = useFavouriteTransfersRefetch();

  // To download fund transfer details
  const { isLoadingDownloadData } = useDownloadTransactionDetails();

  // Define api call conditions
  const shouldMakeTransfer = Boolean(service);

  // Get number format
  const numberFormat = language.cultureName;

  // actions
  const onToggleDetails = useCallback(() => {
    setIsDetailsOpen(isOpen => !isOpen);
  }, [setIsDetailsOpen]);

  const onToggleTemplateSaveBtn = () => setShowTemplateSaveBtn(templateSave => !templateSave);

  const navigateToDashboard = useCallback(() => {
    navigate(getRoutePath({ routeKey: 'dashboard', customerId: (companyId as string) || customerId }));
  }, [customerId, companyId, navigate]);

  const onToggleTemplate = useCallback(() => {
    setIsTemplateOpen(isOpen => !isOpen);
  }, [setIsTemplateOpen]);

  // make transfer request endpoint
  const {
    data: makeTransferData,
    error: makeTransferError,
    isLoading: isLoadingMakeTransfer,
  } = useInvokeSWRService<MakeTransferResponseBody>(
    service.MakeTransfer,
    {
      requestId: transferConfirmData.values.requestId,
      meta: {
        timeStamp,
      },
    },
    shouldMakeTransfer,
    'transferService.MakeTransfer',
  );

  // get transaction's details when drawer is open
  const shouldMakeTransactionDetailsCall = Boolean(makeTransferData?.orderCode && isDetailsOpen);
  const {
    data: transactionDetailsData,
    error: transactionDetailsError,
    isLoading: isLoadingTransactionDetails,
    mutate: transactionDetailsDataMutation,
  } = useInvokeSWRService<ConsolidatedTransactionDetails>(
    transactionService.GetConsolidatedTransactionDetails,
    {
      orderId: makeTransferData?.orderCode,
      accountNumber: transferConfirmData?.values?.routingFormData?.debitAccountNumber,
      meta: {
        timeStamp,
      },
    },
    shouldMakeTransactionDetailsCall,
    'transactionService.GetConsolidatedTransactionDetails',
  );

  // get transaction documents when transaction details is successful and the transaction has documents
  const shouldMakeTransactionDocumentsCall = Boolean(
    transactionDetailsData?.hasDocuments && transactionDetailsData?.globalOrderId,
  );

  const {
    data: transactionDocuments,
    error: transactionDocumentsError,
    isLoading: isLoadingTransactionDocuments,
    mutate: transactionDocumentsMutation,
  } = useInvokeSWRService<TransactionDocument[]>(
    transactionService.GetTransactionDocuments,
    {
      orderId: transactionDetailsData?.globalOrderId,
      customerId,
      meta: {
        timeStamp,
      },
    },
    shouldMakeTransactionDocumentsCall,
    'transactionService.GetDocuments',
  );

  const onDocumentFetchRetry: React.MouseEventHandler<HTMLAnchorElement> &
    React.MouseEventHandler<HTMLSpanElement> = () => {
    if (transactionDocumentsMutation) {
      transactionDocumentsMutation();
    }
  };

  const shouldMakeExchangeAmountCall =
    !!transactionDetailsData &&
    exchangeAmountPredicator(transactionDetailsData?.processingStatus) &&
    (transactionDetailsData?.fromCurrencyId !== transactionDetailsData?.toCurrencyId ||
      (transactionDetailsData?.internalDestinationCurrencyId !== 0 &&
        transactionDetailsData?.internalDestinationCurrencyId !== transactionDetailsData?.fromCurrencyId));

  const {
    data: exchangeCalculateData,
    error: exchangeCalculateError,
    isLoading: isLoadingExchangeCalculate,
  } = useInvokeSWRService<ExchangeCalculateResponseBody>(
    service.ExchangeCalculate,
    {
      fromCurrencyId: transactionDetailsData?.fromCurrencyId,
      toCurrencyId:
        transactionDetailsData?.internalDestinationCurrencyId !== 0 &&
        transactionDetailsData?.internalDestinationCurrencyId !== transactionDetailsData?.fromCurrencyId
          ? transactionDetailsData?.internalDestinationCurrencyId
          : transactionDetailsData?.toCurrencyId,
      amount: transactionDetailsData?.amount,
      isSideTypeBuy: transactionDetailsData?.accountCurrency === transactionDetailsData?.exchangeCurrency,
    },
    shouldMakeExchangeAmountCall,
    'transactionService.ExchangeCalculate',
  );

  const shouldFetchOrderCharges =
    !!transactionDetailsData &&
    isDefined(transactionDetailsData?.orderId) &&
    (transactionDetailsData?.processingStatus === '0' ||
      transactionDetailsData?.processingStatus === '4' ||
      transactionDetailsData?.processingStatus === '5');
  const {
    data: orderCharges,
    isLoading: isLoadingOrderCharges,
    error: orderChargesError,
  } = useInvokeSWRService<OrderChargesResponseBody>(
    service.GetOrderCharges,
    {
      orderCode: String(transactionDetailsData?.orderId),
    },
    shouldFetchOrderCharges,
    'transferService.GetOrderCharges',
  );
  const { commisionAmount, totalCharges } = useTransactionCharges(
    transactionDetailsData!,
    orderCharges,
    orderChargesError,
  );

  // ui rendering logic
  const {
    routingResponseData,
    otherMandatoryDataForUI,
    routingFormData,
    shouldShowExchangeRates,
    isOneColumnGrid,
    renderBeneficiaryBankName,
    amount,
    isTransactionSettled,
  } = useFundsTransferUILogicDisplayer('transferConfirm', makeTransferData, transactionDetailsData);

  const {
    forecastResponseData,
    isDebitCurrencySameWithOrderCurrency,
    creditCurrency,
    debitCurrency,
    exchangeCurrency,
  } = useFundsTransferUILogicDisplayer('initTransfer', makeTransferData, transactionDetailsData);

  const {
    data: createNewFavoriteTransactionData,
    error: createNewFavoriteTransactionError,
    isLoading: isLoadingCreateNewFavoriteTransaction,
    mutate,
  } = useInvokeSWRService<NewFavouriteAppTransactionBody>(
    favouriteTransfersService.CreateNewFavouriteTransfer,
    {
      appTransactionUniqueId: routingResponseData?.appTransactionId,
      name: favoureTransferPostData?.name,
      customerId: (companyId as string) || customerId,
      shared: favoureTransferPostData?.isShared,
      successCallback: favouriteTransferRefetch,
      meta: {
        timeStamp,
      },
    },
    shouldSaveTransferAsFavourite,
    'favouriteTransferService.CreateNewFavouriteTransfer',
  );

  const labels: GenericObject = {
    orderId: t('fundsTransferPage.TransferFlow.TransactionId'),
    accountCurrency: t('fundsTransferPage.Forms.Currency'),
    exchangeCurrency: t('fundsTransferPage.Forms.Currency'),
    status: t('fundsTransferPage.TransferFlow.Status'),
    type: t('fundsTransferPage.TransferFlow.Type'),
    commission: t('fundsTransferPage.TransferFlow.Commission'),
    totalCharges: t('fundsTransferPage.TransferFlow.TransferTotalCharges'),
    charges: t('fundsTransferPage.TransferFlow.Charges'),
    beneficiaryFullName: t('fundsTransferPage.TransferFlow.Beneficiary.FullName'),
    beneficiaryCountry: t('fundsTransferPage.TransferFlow.BeneficiaryCountry'),
    bankName: t('fundsTransferPage.TransferFlow.BenBankName'),
    bankBic: t('fundsTransferPage.TransferFlow.BankBIC'),
    beneficiaryAddress: t('fundsTransferPage.TransferFlow.BeneficiaryAddress'),
    beneficiaryBankCountry: t('fundsTransferPage.TransferFlow.BeneficiaryBankCountry'),
    beneficiaryCity: t('fundsTransferPage.TransferFlow.BeneficiaryCity'),
    details: t('fundsTransferPage.TransferFlow.Comments'),
    bop: t('fundsTransferPage.TransferFlow.BopCode'),
    sortCode: t('fundsTransferPage.TransferFlow.SortCode'),
    cor: t('fundsTransferPage.TransferFlow.COR'),
    bicBankCode: t('fundsTransferPage.TransferFlow.BIK'),
    inn: t('fundsTransferPage.TransferFlow.INN'),
    kpp: t('fundsTransferPage.TransferFlow.KPP'),
    voDescription: t('fundsTransferPage.TransferFlow.VO'),
  };

  const isLoadingTransactionDetailsData =
    isLoadingTransactionDetails || isLoadingExchangeCalculate || isLoadingOrderCharges;

  const makeTransferRenderer = () => {
    if (makeTransferError && !isEmpty(makeTransferError)) {
      return (
        <ErrorContent
          code={makeTransferError.code}
          message={t('fundsTransferPage.Requests.Huston.Message')}
          subtitle={t('fundsTransferPage.Requests.MakeTransfer.Error.Message')}
          onClickBtn={navigateToDashboard}
        />
      );
    }

    if (transferConfirmData.values.hasUserSelectedDealerRate) {
      return (
        <RequestDealerRateSuccessInfo
          isLoading={isLoadingMakeTransfer}
          onBack={navigateToDashboard}
          isMobile={isMobile}
          t={t}
        />
      );
    }

    if (isMobile || isBelowSpecificBreakpoint) {
      return (
        <MobileContent
          amount={Number(routingFormData.orderAmount)}
          currency={exchangeCurrency.shortDescription}
          numberFormat={numberFormat}
          navigateToDashboard={navigateToDashboard}
          onToggleDetails={onToggleDetails}
          onToggleTemplate={onToggleTemplate}
          t={t}
          isLoading={isLoadingMakeTransfer}
          isTransactionSettled={isTransactionSettled()}
          showTemplateSaveBtn={showTemplateSaveBtn}
          transactionId={makeTransferData?.orderCode}
        />
      );
    }

    return (
      <DesktopContent
        amount={Number(routingFormData.orderAmount)}
        currency={exchangeCurrency.shortDescription}
        numberFormat={numberFormat}
        navigateToDashboard={navigateToDashboard}
        onToggleDetails={onToggleDetails}
        onToggleTemplate={onToggleTemplate}
        t={t}
        isLoading={isLoadingMakeTransfer}
        isTransactionSettled={isTransactionSettled()}
        showTemplateSaveBtn={showTemplateSaveBtn}
        transactionId={makeTransferData?.orderCode}
      />
    );
  };

  return (
    <ContentWrapper
      hasDivider={false}
      boxProps={{
        display: 'flex',
        paddingBottom: 2,
        margin: 'auto',
        height: '100%',
        width: '100%',
      }}
      paperProps={{ elevation: 0, classes: { root: classes.paperClasses } }}>
      <Grid
        container
        className={classes.container}
        justifyContent={isMobile ? 'center' : makeTransferData || isLoadingMakeTransfer ? 'space-between' : 'center'}>
        {makeTransferRenderer()}
        <GenericDrawer
          anchor='bottom'
          disableMobileDrawerHeight={true}
          stickDrawerTitleToLeft={isMobile}
          isOpen={isDetailsOpen}
          onToggleDrawer={onToggleDetails}
          DownloadTransactionDetailsComponent={() => (
            <TransactionDetailsDrawerFileDownload
              isLoading={isLoadingDownloadData}
              transactionDetails={transactionDetailsData}
              isAuthorization={false}
            />
          )}
          title={t('fundsTransfer.SuccessStep.Drawer.Title')}>
          {(transactionDetailsError && !isEmpty(transactionDetailsError)) ||
            (exchangeCalculateError && !isEmpty(exchangeCalculateError) && (
              <ErrorContent
                code={transactionDetailsError?.code || exchangeCalculateError?.code}
                message={t('fundsTransferPage.Requests.Huston.Message')}
                subtitle={t('fundsTransferPage.Requests.FundTransferSingleTransaction.Error.Message')}
                btnLabel={t('fundsTransferPage.Requests.FundTransferSingleTransaction.Error.BtnLabel')}
                onClickBtn={transactionDetailsDataMutation}
              />
            ))}
          {isEmpty(transactionDetailsError) && (
            <Grid container className={classes.subContainer}>
              <Grid item xs={isMobile ? 12 : 5} className={classes.subItemContainer}>
                <Box
                  pt={isMobile ? 4 : 6}
                  pl={isMobile ? 3 : 10}
                  pr={isMobile ? 3 : 5}
                  pb={isMobile ? 4 : 0}
                  flex={isMobile ? 0 : 1}
                  className={clsx(classes.leftContainer)}>
                  <SkeletonTypography
                    hasSkeleton={isLoadingTransactionDetailsData}
                    typographyProps={{ variant: isMobile ? 'h5' : 'h4' }}>
                    {amount}
                  </SkeletonTypography>
                  <ExchangeRatesInfo
                    t={t}
                    isMobile={isMobile}
                    accountCurrency={debitCurrency.shortDescription}
                    exchangeCurrency={creditCurrency.shortDescription}
                    initialAmountValue={transactionDetailsData?.amount}
                    rateCurrencyISO={transactionDetailsData?.rateCurrencyISO}
                    exchangeAmountValue={
                      isDebitCurrencySameWithOrderCurrency
                        ? forecastResponseData.outCreditAmount
                        : forecastResponseData.currentOrderDebitedAmount
                    }
                    exchangeAmountCurrency={
                      isDebitCurrencySameWithOrderCurrency
                        ? creditCurrency.shortDescription
                        : debitCurrency.shortDescription
                    }
                    exchangeRate={transactionDetailsData?.trxRateGiven}
                    shouldRoundSecondUnit
                    isLoading={isLoadingTransactionDetailsData}
                    shouldShowExchangeRates={
                      shouldShowExchangeRates && debitCurrency?.shortDescription !== creditCurrency?.shortDescription
                    }
                  />
                  <Box mt={isMobile ? 3 : 6}>
                    <CardHeading heading={t('fundsTransferPage.TransferFlow.From.Text')} hideOnMobile={false}>
                      <CardGeneric spacing='dense'>
                        <Box className={classes.fromToAccountContainer}>
                          <LabelValue
                            label={transactionDetailsData?.alias || transactionDetailsData?.accountProductDescription}
                            value={transactionDetailsData?.fromAccount}
                            valueAreaLabel={`${transactionDetailsData?.alias} ${transactionDetailsData?.accountProductDescription} ${transactionDetailsData?.fromAccount}`}
                            labelColor={isMobile ? 'primary' : 'textSecondary'}
                            labelSize='small'
                            valueSize='medium'
                            valueColor={isMobile ? 'secondary' : 'primary'}
                            labelAlignment='left'
                            valueAlignment='left'
                            hasSkeleton={isLoadingTransactionDetailsData}
                          />
                          <Box ml={1} mt={1}>
                            {!isLoadingTransactionDetailsData && <ResidentBankLogo resident='EcommBX' />}
                          </Box>
                        </Box>
                      </CardGeneric>
                    </CardHeading>
                  </Box>
                  <Box mt={3}>
                    <CardHeading heading={t('fundsTransferPage.TransferFlow.To.Text')} hideOnMobile={false}>
                      <CardGeneric spacing='dense'>
                        <Box className={classes.fromToAccountContainer}>
                          <LabelValue
                            label={transactionDetailsData?.beneficiaryFullName}
                            value={transactionDetailsData?.toAccount}
                            valueAreaLabel={`${transactionDetailsData?.beneficiaryFullName} ${transactionDetailsData?.toAccount}`}
                            labelColor={isMobile ? 'primary' : 'textSecondary'}
                            labelSize='small'
                            valueSize='medium'
                            valueColor={isMobile ? 'secondary' : 'primary'}
                            labelAlignment='left'
                            valueAlignment='left'
                            hasSkeleton={isLoadingTransactionDetailsData}
                          />
                          <Box ml={1} mt={1}>
                            {!isLoadingTransactionDetailsData && renderBeneficiaryBankName()}
                          </Box>
                        </Box>
                      </CardGeneric>
                    </CardHeading>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={isMobile ? 12 : 7} className={clsx(classes.fullHeight, classes.subItemContainer)}>
                <List
                  className={clsx(classes.list, {
                    [classes.oneColumn]: isOneColumnGrid,
                    [classes.twoColumns]: !isOneColumnGrid && !isMobile,
                  })}>
                  <ListItem className={classes.listItem}>
                    <LabelValue
                      label={labels.orderId}
                      value={transactionDetailsData?.orderId}
                      valueAreaLabel={`${labels.orderId} ${transactionDetailsData?.orderId}`}
                      labelColor='textPrimary'
                      labelSize='small'
                      valueSize='small'
                      valueColor='secondary'
                      labelAlignment='left'
                      valueAlignment='left'
                      hasSkeleton={isLoadingTransactionDetailsData}
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <LabelValue
                      label={labels.type}
                      value={
                        <Chip
                          label={
                            transactionDetailsData?.type === 'O'
                              ? t('Transaction.Transfer.Debit.Type')
                              : t('Transaction.Transfer.Credit.Type')
                          }
                          bgColor={transactionDetailsData?.type === 'O' ? 'error' : 'success'}
                        />
                      }
                      valueAreaLabel={`${labels.type} ${
                        transactionDetailsData?.type === 'O'
                          ? t('Transaction.Transfer.Debit.Type')
                          : t('Transaction.Transfer.Credit.Type')
                      }`}
                      labelColor='textPrimary'
                      labelSize='small'
                      valueSize='small'
                      valueColor='secondary'
                      labelAlignment='left'
                      valueAlignment='left'
                      hasSkeleton={isLoadingTransactionDetailsData}
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <LabelValue
                      label={labels.status}
                      value={
                        transactionDetailsData ? (
                          <TransactionOrderStatusChip statusSerialNumber={Number(transactionDetailsData?.status)} />
                        ) : (
                          '-'
                        )
                      }
                      valueAreaLabel={`${labels.status} ${
                        (transactionDetailsData &&
                          !isEmpty(otherMandatoryDataForUI.orderStatuses) &&
                          (otherMandatoryDataForUI.orderStatuses as OrderStatus[]).find(
                            os => os.serialNumber === Number(transactionDetailsData?.status),
                          )?.description) ??
                        transactionDetailsData?.status ??
                        '-'
                      }`}
                      labelColor='textPrimary'
                      labelSize='small'
                      valueSize='small'
                      valueColor='secondary'
                      labelAlignment='left'
                      valueAlignment='left'
                      hasSkeleton={isLoadingTransactionDetailsData}
                    />
                  </ListItem>
                  <ListItem className={classes.detailsListItem}>
                    <LabelValue
                      label={labels.commission}
                      value={commisionAmount}
                      valueAreaLabel={commisionAmount}
                      labelColor='textPrimary'
                      labelSize='small'
                      valueSize='small'
                      valueColor='secondary'
                      labelAlignment='left'
                      valueAlignment='left'
                      hasSkeleton={isLoadingTransactionDetailsData}
                    />
                  </ListItem>
                  <ListItem className={classes.detailsListItem}>
                    <LabelValue
                      label={labels.totalCharges}
                      value={totalCharges}
                      valueAreaLabel={totalCharges}
                      labelColor='textPrimary'
                      labelSize='small'
                      valueSize='small'
                      valueColor='secondary'
                      labelAlignment='left'
                      valueAlignment='left'
                      hasSkeleton={isLoadingTransactionDetailsData}
                    />
                  </ListItem>
                  {transactionDetailsData?.accountCurrency && (
                    <ListItem className={classes.listItem}>
                      <LabelValue
                        label={labels.accountCurrency}
                        value={transactionDetailsData?.accountCurrency}
                        valueAreaLabel={`${labels.accountCurrency} ${transactionDetailsData?.accountCurrency}`}
                        labelColor='textPrimary'
                        labelSize='small'
                        valueSize='small'
                        valueColor='secondary'
                        labelAlignment='left'
                        valueAlignment='left'
                        hasSkeleton={isLoadingTransactionDetailsData}
                      />
                    </ListItem>
                  )}
                  <ListItem className={classes.listItem}>
                    <LabelValue
                      label={labels.charges}
                      value={transactionDetailsData?.charges}
                      valueAreaLabel={`${labels.charges} ${transactionDetailsData?.charges}`}
                      labelColor='textPrimary'
                      labelSize='small'
                      valueSize='small'
                      valueColor='secondary'
                      labelAlignment='left'
                      valueAlignment='left'
                      hasSkeleton={isLoadingTransactionDetailsData}
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <LabelValue
                      label={labels.beneficiaryFullName}
                      value={transactionDetailsData?.beneficiaryFullName}
                      valueAreaLabel={`${labels.beneficiaryFullName} ${transactionDetailsData?.beneficiaryFullName}`}
                      labelColor='textPrimary'
                      labelSize='small'
                      valueSize='small'
                      valueColor='secondary'
                      labelAlignment='left'
                      valueAlignment='left'
                      hasSkeleton={isLoadingTransactionDetailsData}
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <LabelValue
                      label={labels.beneficiaryAddress}
                      value={transactionDetailsData?.beneficiaryAddress}
                      valueAreaLabel={`${labels.beneficiaryAddress} ${transactionDetailsData?.beneficiaryAddress}`}
                      labelColor='textPrimary'
                      labelSize='small'
                      valueSize='small'
                      valueColor='secondary'
                      labelAlignment='left'
                      valueAlignment='left'
                      hasSkeleton={isLoadingTransactionDetailsData}
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <LabelValue
                      label={labels.beneficiaryCity}
                      value={transactionDetailsData?.beneficiaryCity}
                      valueAreaLabel={`${labels.beneficiaryCity} ${transactionDetailsData?.beneficiaryCity}`}
                      labelColor='textPrimary'
                      labelSize='small'
                      valueSize='small'
                      valueColor='secondary'
                      labelAlignment='left'
                      valueAlignment='left'
                      hasSkeleton={isLoadingTransactionDetailsData}
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <LabelValue
                      label={labels.beneficiaryCountry}
                      value={transactionDetailsData?.beneficiaryCountry}
                      valueAreaLabel={`${labels.beneficiaryCountry} ${transactionDetailsData?.beneficiaryCountry}`}
                      labelColor='textPrimary'
                      labelSize='small'
                      valueSize='small'
                      valueColor='secondary'
                      labelAlignment='left'
                      valueAlignment='left'
                      hasSkeleton={isLoadingTransactionDetailsData}
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <LabelValue
                      label={labels.bankName}
                      value={transactionDetailsData?.bankName}
                      valueAreaLabel={`${labels.bankName} ${transactionDetailsData?.bankName}`}
                      labelColor='textPrimary'
                      labelSize='small'
                      valueSize='small'
                      valueColor='secondary'
                      labelAlignment='left'
                      valueAlignment='left'
                      hasSkeleton={isLoadingTransactionDetailsData}
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <LabelValue
                      label={labels.bankBic}
                      value={transactionDetailsData?.bankBic}
                      valueAreaLabel={`${labels.bankBic} ${transactionDetailsData?.bankBic}`}
                      labelColor='textPrimary'
                      labelSize='small'
                      valueSize='small'
                      valueColor='secondary'
                      labelAlignment='left'
                      valueAlignment='left'
                      hasSkeleton={isLoadingTransactionDetailsData}
                    />
                  </ListItem>
                  <ListItem className={classes.listItem}>
                    <LabelValue
                      label={labels.beneficiaryBankCountry}
                      value={transactionDetailsData?.country}
                      valueAreaLabel={`${labels.beneficiaryBankCountry} ${transactionDetailsData?.country}`}
                      labelColor='textPrimary'
                      labelSize='small'
                      valueSize='small'
                      valueColor='secondary'
                      labelAlignment='left'
                      valueAlignment='left'
                      hasSkeleton={isLoadingTransactionDetailsData}
                    />
                  </ListItem>
                  {transactionDetailsData?.details && (
                    <ListItem className={classes.listItem}>
                      <LabelValue
                        label={labels.details}
                        value={transactionDetailsData?.details}
                        valueAreaLabel={`${labels.details} ${transactionDetailsData?.details}`}
                        labelColor='textPrimary'
                        labelSize='small'
                        valueSize='small'
                        valueColor='secondary'
                        labelAlignment='left'
                        valueAlignment='left'
                        hasSkeleton={isLoadingTransactionDetailsData}
                      />
                    </ListItem>
                  )}
                  {transactionDetailsData?.bop && (
                    <ListItem className={classes.listItem}>
                      <LabelValue
                        label={labels.bop}
                        value={transactionDetailsData?.bop}
                        valueAreaLabel={`${labels.bop} ${transactionDetailsData?.bop}`}
                        labelColor='textPrimary'
                        labelSize='small'
                        valueSize='small'
                        valueColor='secondary'
                        labelAlignment='left'
                        valueAlignment='left'
                        hasSkeleton={isLoadingTransactionDetailsData}
                      />
                    </ListItem>
                  )}
                  {transactionDetailsData?.sortCode && (
                    <ListItem className={classes.listItem}>
                      <LabelValue
                        label={labels.sortCode}
                        value={transactionDetailsData?.sortCode}
                        valueAreaLabel={`${labels.sortCode} ${transactionDetailsData?.sortCode}`}
                        labelColor='textPrimary'
                        labelSize='small'
                        valueSize='small'
                        valueColor='secondary'
                        labelAlignment='left'
                        valueAlignment='left'
                        hasSkeleton={isLoadingTransactionDetailsData}
                      />
                    </ListItem>
                  )}
                  {transactionDetailsData?.cor && (
                    <ListItem className={classes.listItem}>
                      <LabelValue
                        label={labels.cor}
                        value={transactionDetailsData?.cor}
                        valueAreaLabel={`${labels.cor} ${transactionDetailsData?.cor}`}
                        labelColor='textPrimary'
                        labelSize='small'
                        valueSize='small'
                        valueColor='secondary'
                        labelAlignment='left'
                        valueAlignment='left'
                        hasSkeleton={isLoadingTransactionDetailsData}
                      />
                    </ListItem>
                  )}
                  {transactionDetailsData?.bicBankCode && (
                    <ListItem className={classes.listItem}>
                      <LabelValue
                        label={labels.bicBankCode}
                        value={transactionDetailsData?.bicBankCode}
                        valueAreaLabel={`${labels.bicBankCode} ${transactionDetailsData?.bicBankCode}`}
                        labelColor='textPrimary'
                        labelSize='small'
                        valueSize='small'
                        valueColor='secondary'
                        labelAlignment='left'
                        valueAlignment='left'
                        hasSkeleton={isLoadingTransactionDetailsData}
                      />
                    </ListItem>
                  )}
                  {transactionDetailsData?.inn && (
                    <ListItem className={classes.listItem}>
                      <LabelValue
                        label={labels.inn}
                        value={transactionDetailsData?.inn}
                        valueAreaLabel={`${labels.inn} ${transactionDetailsData?.inn}`}
                        labelColor='textPrimary'
                        labelSize='small'
                        valueSize='small'
                        valueColor='secondary'
                        labelAlignment='left'
                        valueAlignment='left'
                        hasSkeleton={isLoadingTransactionDetailsData}
                      />
                    </ListItem>
                  )}
                  {transactionDetailsData?.kpp && (
                    <ListItem className={classes.listItem}>
                      <LabelValue
                        label={labels.kpp}
                        value={transactionDetailsData?.kpp}
                        valueAreaLabel={`${labels.kpp} ${transactionDetailsData?.kpp}`}
                        labelColor='textPrimary'
                        labelSize='small'
                        valueSize='small'
                        valueColor='secondary'
                        labelAlignment='left'
                        valueAlignment='left'
                        hasSkeleton={isLoadingTransactionDetailsData}
                      />
                    </ListItem>
                  )}
                  {transactionDetailsData?.voDescription && (
                    <ListItem className={classes.listItem}>
                      <LabelValue
                        label={labels.voDescription}
                        value={transactionDetailsData?.voDescription}
                        valueAreaLabel={`${labels.voDescription} ${transactionDetailsData?.voDescription}`}
                        labelColor='textPrimary'
                        labelSize='small'
                        valueSize='small'
                        valueColor='secondary'
                        labelAlignment='left'
                        valueAlignment='left'
                        hasSkeleton={isLoadingTransactionDetailsData}
                      />
                    </ListItem>
                  )}
                  {transactionDetailsData?.hasDocuments &&
                  !transactionDocumentsError &&
                  isEmpty(transactionDocumentsError) ? (
                    <ListItem className={classes.listItem}>
                      <Box display='flex' flexDirection='column'>
                        <LabelValue
                          label={t('fundsTransferPage.TransferFlow.UploadedDocuments')}
                          labelColor='textPrimary'
                          labelSize='small'
                          valueAreaLabel={t('fundsTransferPage.TransferFlow.UploadedDocuments')}
                          valueSize='small'
                          valueColor='secondary'
                          labelAlignment='left'
                          valueAlignment='left'
                          hasSkeleton={isLoadingTransactionDetailsData || isLoadingTransactionDocuments}
                          value={transactionDocuments?.map(({ fileName, referenceNumber }) => (
                            <Box key={referenceNumber} mt={1}>
                              <Typography variant='body2' color='textSecondary'>
                                {fileName}
                              </Typography>
                            </Box>
                          ))}
                        />
                      </Box>
                    </ListItem>
                  ) : (
                    !isEmpty(transactionDocumentsError) && (
                      <ListItem className={classes.listItem}>
                        <Alert
                          icon={false}
                          severity='error'
                          variant='filled'
                          action={
                            <Box
                              title={t('fundsTransferPage.Requests.FundTransferSingleTransactionDocuments.Retry')}
                              onClick={onDocumentFetchRetry}
                              className={classes.retry}>
                              {t('fundsTransferPage.Requests.FundTransferSingleTransactionDocuments.Retry')}
                            </Box>
                          }>
                          {t('fundsTransferPage.Requests.FundTransferSingleTransactionDocuments.Error.Message')}
                        </Alert>
                      </ListItem>
                    )
                  )}
                </List>
              </Grid>
            </Grid>
          )}
        </GenericDrawer>
        <SaveTemplateDialog
          error={createNewFavoriteTransactionError}
          isLoading={isLoadingCreateNewFavoriteTransaction}
          isOpen={isTemplateOpen}
          onToggle={onToggleTemplate}
          mutateFn={mutate}
          shouldSaveTransferAsFavourite={shouldSaveTransferAsFavourite}
          setShouldSaveTransferAsFavorite={setShouldSaveTransferAsFavorite}
          setFavouriteTransferPostData={setFavouriteTransferPostData}
          createNewFavoriteTransactionData={createNewFavoriteTransactionData}
          onToggleTemplateSaveBtn={onToggleTemplateSaveBtn}
        />
      </Grid>
    </ContentWrapper>
  );
};

export { TransferComplete };
